import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  notificationsApi,
  NotificationItem,
  NotificationListResponse,
  NotificationPreference,
  UpdatePreferencePayload
} from '@services/notificationsApi';
import { useAuth } from '@context/AuthContext';

interface UseNotificationsOptions {
  initialPage?: number;
  pageSize?: number;
  autoRefreshMs?: number;
}

interface UseNotificationsResult {
  notifications: NotificationItem[];
  pagination: NotificationListResponse['pagination'];
  unreadCount: number;
  isLoading: boolean;
  error: string | null;
  loadPage: (page: number) => Promise<void>;
  refresh: () => Promise<void>;
  markRead: (id: string) => Promise<void>;
  markAllRead: () => Promise<void>;
  preferences: NotificationPreference[];
  loadPreferences: () => Promise<void>;
  updatePreferences: (preferences: UpdatePreferencePayload[]) => Promise<void>;
}

export function useNotifications(options?: UseNotificationsOptions): UseNotificationsResult {
  const {
    initialPage = 1,
    pageSize = 10,
    autoRefreshMs = 0
  } = options ?? {};

  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [pagination, setPagination] = useState<NotificationListResponse['pagination']>({
    page: initialPage,
    limit: pageSize,
    total: 0,
    totalPages: 0
  });
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preferences, setPreferences] = useState<NotificationPreference[]>([]);
  const refreshTimerRef = useRef<number | null>(null);
  const { authState } = useAuth();
  const isAuthenticated = authState.isAuthenticated;

  const load = useCallback(
    async (page = pagination.page) => {
      if (!isAuthenticated) {
        setNotifications([]);
        setPagination(prev => ({
          ...prev,
          page: 1,
          total: 0,
          totalPages: 0
        }));
        setUnreadCount(0);
        return;
      }

      setIsLoading(true);
      setError(null);
      try {
        const response = await notificationsApi.list({
          page,
          limit: pagination.limit
        });
        setNotifications(response.notifications);
        setPagination(response.pagination);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load notifications.');
        setNotifications([]);
        setPagination(prev => ({
          ...prev,
          page: 1,
          total: 0,
          totalPages: 0
        }));
      } finally {
        setIsLoading(false);
      }
    },
    [isAuthenticated, pagination.limit, pagination.page]
  );

  const loadUnreadCount = useCallback(async () => {
    if (!isAuthenticated) {
      setUnreadCount(0);
      return;
    }

    try {
      const count = await notificationsApi.unreadCount();
      setUnreadCount(count);
    } catch (err) {
      console.error('Failed to load unread notifications count', err);
    }
  }, [isAuthenticated]);

  const refresh = useCallback(async () => {
    if (!isAuthenticated) {
      setNotifications([]);
      setUnreadCount(0);
      return;
    }
    await Promise.all([load(), loadUnreadCount()]);
  }, [isAuthenticated, load, loadUnreadCount]);

  useEffect(() => {
    if (isAuthenticated) {
      void refresh();
    } else {
      setNotifications([]);
      setUnreadCount(0);
      setPagination(prev => ({
        ...prev,
        page: 1,
        total: 0,
        totalPages: 0
      }));
    }
  }, [isAuthenticated, refresh]);

  useEffect(() => {
    if (!autoRefreshMs) return;
    if (refreshTimerRef.current) {
      window.clearInterval(refreshTimerRef.current);
    }
    refreshTimerRef.current = window.setInterval(() => {
      if (isAuthenticated) {
        void refresh();
      }
    }, autoRefreshMs);

    return () => {
      if (refreshTimerRef.current) {
        window.clearInterval(refreshTimerRef.current);
      }
    };
  }, [autoRefreshMs, isAuthenticated, refresh]);

  const loadPage = useCallback(
    async (page: number) => {
      if (!isAuthenticated) {
        return;
      }
      await load(page);
      await loadUnreadCount();
    },
    [isAuthenticated, load, loadUnreadCount]
  );

  const markRead = useCallback(
    async (id: string) => {
      if (!isAuthenticated) {
        return;
      }
      try {
        await notificationsApi.markRead(id);
        setNotifications((prev) =>
          prev.map((notification) =>
            notification.id === id ? { ...notification, read: true } : notification
          )
        );
        setUnreadCount((prev) => Math.max(0, prev - 1));
      } catch (err) {
        console.error('Failed to mark notification as read', err);
      }
    },
    [isAuthenticated]
  );

  const markAllRead = useCallback(async () => {
    if (!isAuthenticated) {
      return;
    }
    try {
      await notificationsApi.markAllRead();
      setNotifications((prev) => prev.map((notification) => ({ ...notification, read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Failed to mark notifications as read', err);
    }
  }, [isAuthenticated]);

  const loadPreferences = useCallback(async () => {
    if (!isAuthenticated) {
      setPreferences([]);
      return;
    }
    try {
      const prefs = await notificationsApi.getPreferences();
      setPreferences(prefs);
    } catch (err) {
      console.error('Failed to load notification preferences', err);
    }
  }, [isAuthenticated]);

  const updatePreferences = useCallback(async (updates: UpdatePreferencePayload[]) => {
    if (!isAuthenticated) {
      return;
    }
    try {
      const updated = await notificationsApi.updatePreferences(updates);
      setPreferences(updated);
    } catch (err) {
      console.error('Failed to update notification preferences', err);
      throw err;
    }
  }, [isAuthenticated]);

  return useMemo(
    () => ({
      notifications,
      pagination,
      unreadCount,
      isLoading,
      error,
      loadPage,
      refresh,
      markRead,
      markAllRead,
      preferences,
      loadPreferences,
      updatePreferences
    }),
    [
      notifications,
      pagination,
      unreadCount,
      isLoading,
      error,
      loadPage,
      refresh,
      markRead,
      markAllRead,
      preferences,
      loadPreferences,
      updatePreferences
    ]
  );
}
